import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { useAuth } from "../../../providers/AuthProvider";
import { Button } from "../../../app/components/ui/button";
import { Badge } from "../../../app/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../app/components/ui/sheet";

function formatMoney(amount: string | number | null | undefined) {
  const n = typeof amount === "string" ? Number(amount) : typeof amount === "number" ? amount : 0;
  return `₹${n.toFixed(2)}`;
}

export function SalesDetailDrawer({
  open,
  onOpenChange,
  saleId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saleId: string | null;
}) {
  const { token } = useAuth();

  const detailQuery = useQuery({
    queryKey: ["sale", saleId],
    enabled: Boolean(open && token && saleId),
    queryFn: async () => {
      if (!token) throw new Error("Not authenticated");
      if (!saleId) throw new Error("Missing sale id");
      return api.sales.detail({ token, id: saleId });
    },
  });

  const sale = detailQuery.data;

  const downloadPdf = useCallback(async () => {
    if (!token || !saleId) return;
    try {
      const blob = await api.sales.downloadInvoice({ token, id: saleId });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${sale?.invoice_number ?? saleId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to download invoice");
    }
  }, [token, saleId, sale?.invoice_number]);

  const printPdf = useCallback(async () => {
    if (!token || !saleId) return;
    try {
      const blob = await api.sales.downloadInvoice({ token, id: saleId });
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) {
        toast.message("Popup blocked. Please allow popups to print.");
        return;
      }
      window.setTimeout(() => {
        try {
          w.focus();
          w.print();
        } catch {
          // ignore
        }
      }, 600);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to open invoice for printing");
    }
  }, [token, saleId]);

  const header = useMemo(() => {
    if (!sale) return null;
    return {
      invoice: sale.invoice_number ?? `#${sale.id}`,
      createdAt: sale.created_at,
      customer: sale.customer?.name ?? "-",
      phone: sale.customer?.phone ?? "-",
      status: sale.status ?? "completed",
      payment: sale.payment?.method ?? "-",
    };
  }, [sale]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Sale details</SheetTitle>
          {header ? (
            <div className="text-sm text-muted-foreground">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <div>
                  <span className="font-medium text-foreground">{header.invoice}</span>
                </div>
                <div>{header.createdAt ? new Date(header.createdAt).toLocaleString() : "-"}</div>
                <div>{header.customer}</div>
                <div>{header.phone}</div>
                <div>
                  <Badge variant={header.status.toLowerCase() === "cancelled" ? "destructive" : header.status.toLowerCase() === "refunded" ? "secondary" : "default"}>
                    {header.status.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>
          ) : null}
        </SheetHeader>

        <div className="px-4 pb-4 space-y-4 overflow-auto">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => void downloadPdf()} disabled={!saleId}>
              Download PDF
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void printPdf()} disabled={!saleId}>
              Print
            </Button>
            <Button size="sm" variant="outline" disabled>
              Refund (coming soon)
            </Button>
          </div>

          {detailQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Loading sale...</div>
          ) : detailQuery.isError ? (
            <div className="text-sm text-destructive">
              {detailQuery.error instanceof Error ? detailQuery.error.message : "Failed to load sale."}
            </div>
          ) : sale ? (
            <>
              <div className="rounded-lg border p-3 bg-muted/20">
                <div className="text-sm font-medium mb-2">Totals</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">Subtotal</div>
                  <div className="text-right font-medium">{formatMoney(sale.totals.subtotal)}</div>
                  <div className="text-muted-foreground">Discount</div>
                  <div className="text-right font-medium">-{formatMoney(sale.totals.discount)}</div>
                  <div className="text-muted-foreground">Tax</div>
                  <div className="text-right font-medium">{formatMoney(sale.totals.tax)}</div>
                  <div className="text-muted-foreground">Grand total</div>
                  <div className="text-right font-semibold">{formatMoney(sale.totals.grand_total)}</div>
                </div>
              </div>

              <div>
                <div className="text-sm font-medium mb-2">Items</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sale.items.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell>
                          <div className="font-medium">{it.product.name ?? "Item"}</div>
                          <div className="text-xs text-muted-foreground">
                            {it.batches?.[0]?.batch_no ? `Batch: ${it.batches[0].batch_no}` : null}
                            {it.batches?.[0]?.expiry_date ? ` · Exp: ${it.batches[0].expiry_date}` : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{it.quantity}</TableCell>
                        <TableCell className="text-right">{it.unit_price ? formatMoney(it.unit_price) : "-"}</TableCell>
                        <TableCell className="text-right font-medium">{formatMoney(it.line_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div>
                <div className="text-sm font-medium mb-2">GST breakdown</div>
                {sale.gst_breakdown.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No tax breakdown available.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rate</TableHead>
                        <TableHead className="text-right">Taxable</TableHead>
                        <TableHead className="text-right">CGST</TableHead>
                        <TableHead className="text-right">SGST</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sale.gst_breakdown.map((g, idx) => (
                        <TableRow key={`${g.rate}-${idx}`}>
                          <TableCell className="font-medium">{Number(g.rate).toFixed(0)}%</TableCell>
                          <TableCell className="text-right">{formatMoney(g.taxable)}</TableCell>
                          <TableCell className="text-right">{formatMoney(g.cgst)}</TableCell>
                          <TableCell className="text-right">{formatMoney(g.sgst)}</TableCell>
                          <TableCell className="text-right font-medium">{formatMoney(g.total_tax)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              <div className="text-sm text-muted-foreground">
                Payment: <span className="text-foreground font-medium">{(sale.payment.method ?? "-").toUpperCase()}</span>
              </div>
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

